import { useCallback, useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TextInput, DatePicker } from '@/components/ui';
import Select from '@workpay/ds-shared/components/ui/Select';
import MultiSelect from '@workpay/ds-shared/components/ui/MultiSelect';
import FormField from '@/components/ui/Input/FormField';
import { useCountries } from '@/hooks/useCountries';
import { getEmploymentTypes } from '@/services/employmentTypeService';
import { updateLegalEntity } from '@/services/legalEntityService';
import { useNotificationStore } from '@stores/notificationStore';
import { WorkpayApiError } from '@lib/apiEnvelope';
import { useTranslation } from '@/hooks/useTranslation';
import type { LegalEntityDto, UpdateLegalEntityPayload } from '@/types/legalEntity';
import { toApiDateTime } from '@utils/dateHelpers';
import styles from './EntityEditForm.module.scss';

// ============================================
// TYPES
// ============================================

interface EntityEditFormValues {
  name: string;
  entityType: string;
  employmentTypeIds: string[];
  effectiveFrom: string;
}

function useEntityTypeOptions() {
  const { t } = useTranslation();
  return useMemo(() => [
    { label: t('K_OPTION_WORKPAY_OWNED', 'Workpay Owned'), value: 'WORKPAY_OWNED' },
    { label: t('K_OPTION_PARTNER', 'Partner'), value: 'PARTNER' },
  ], [t]);
}

interface EntityEditFormProps {
  entityData: LegalEntityDto;
  onSaved: () => void;
  /** HTML id for the <form> — allows external submit buttons via form attribute */
  formId?: string;
  /** Called when mutation pending state changes — lets parent show loading in modal footer */
  onPendingChange?: (pending: boolean) => void;
}

// ============================================
// COMPONENT
// ============================================

function EntityEditForm({ entityData, onSaved, formId, onPendingChange }: EntityEditFormProps) {
  const { t } = useTranslation();
  const entityTypeOptions = useEntityTypeOptions();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);

  const { countries } = useCountries();

  // Country is locked in edit mode — resolve display name from id
  const countryDisplay = useMemo(() => {
    const id = entityData.countryId;
    if (!id) return '-';
    return entityData.country?.name ?? countries.find((c) => c.id === id)?.name ?? id;
  }, [entityData.country?.name, entityData.countryId, countries]);

  const { data: employmentTypesData, isLoading: employmentTypesLoading } = useQuery({
    queryKey: ['employmentTypes', { size: 100 }],
    queryFn: () => getEmploymentTypes({ size: 100 }),
  });

  const employmentTypeOptions = useMemo(
    () => (employmentTypesData?.content ?? []).map((et) => ({ label: et.name, value: et.id })),
    [employmentTypesData],
  );

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<EntityEditFormValues>({
    defaultValues: {
      name: entityData.name ?? '',
      entityType: entityData.entityType ?? '',
      employmentTypeIds: entityData.employmentTypeIds ?? [],
      effectiveFrom: entityData.effectiveFrom ? entityData.effectiveFrom.split('T')[0] ?? '' : '',
    },
    mode: 'onChange',
  });

  const mutation = useMutation({
    mutationFn: (payload: UpdateLegalEntityPayload) => updateLegalEntity(entityData.id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['legalEntity', entityData.id] });
      void queryClient.invalidateQueries({ queryKey: ['legalEntities'] });
      onSaved();
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  useEffect(() => {
    onPendingChange?.(mutation.isPending);
  }, [mutation.isPending, onPendingChange]);

  const onSubmit = useCallback(
    (data: EntityEditFormValues) => {
      const payload: UpdateLegalEntityPayload = {
        name: data.name,
        entityType: (data.entityType as UpdateLegalEntityPayload['entityType']) || undefined,
        employmentTypeIds: data.employmentTypeIds.length > 0 ? data.employmentTypeIds : undefined,
        effectiveFrom: toApiDateTime(data.effectiveFrom),
      };
      mutation.mutate(payload);
    },
    [mutation],
  );

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)}>
      <div className={styles.formGrid}>
        <TextInput
          label={t('K_LABEL_ENTITY_NAME', 'Entity Name')}
          isRequired
          placeholder={t('K_PLACEHOLDER_ENTITY_NAME', 'Enter entity name')}
          error={errors.name?.message}
          {...register('name', {
            required: t('K_VALIDATION_ENTITY_NAME_REQUIRED', 'Entity name is required'),
            maxLength: { value: 255, message: t('K_VALIDATION_MAX_255', 'Max 255 characters') },
          })}
        />

        <FormField id="country-display" label={t('K_LABEL_COUNTRY', 'Country')}>
          <div className={styles.readOnlyDisplay}>{countryDisplay}</div>
        </FormField>

        <Controller
          name="entityType"
          control={control}
          rules={{ required: t('K_VALIDATION_ENTITY_TYPE_REQUIRED', 'Entity type is required') }}
          render={({ field }) => (
            <Select
              label={t('K_LABEL_ENTITY_TYPE', 'Entity Type')}
              isRequired
              placeholder={t('K_PLACEHOLDER_ENTITY_TYPE', 'Select entity type')}
              options={entityTypeOptions}
              value={field.value}
              onChange={field.onChange}
              error={errors.entityType?.message}
            />
          )}
        />

        <Controller
          name="employmentTypeIds"
          control={control}
          render={({ field }) => (
            <MultiSelect
              label={t('K_LABEL_EMPLOYMENT_TYPES', 'Employment Types')}
              placeholder={t('K_PLACEHOLDER_EMPLOYMENT_TYPES', 'Select employment types')}
              options={employmentTypeOptions}
              value={field.value}
              onChange={field.onChange}
              loading={employmentTypesLoading}
            />
          )}
        />

        <Controller
          name="effectiveFrom"
          control={control}
          rules={{ required: t('K_VALIDATION_EFFECTIVE_FROM_REQUIRED', 'Effective from date is required') }}
          render={({ field }) => (
            <DatePicker
              label={t('K_LABEL_EFFECTIVE_FROM', 'Effective From')}
              isRequired
              value={field.value}
              onChange={field.onChange}
              error={errors.effectiveFrom?.message}
              locale="en-GB"
              fullWidth
            />
          )}
        />
      </div>

    </form>
  );
}

export default EntityEditForm;
